import type { CurrentMembership } from '@/stores/church-store';

import { PERMISSIONS, type PermissionCode } from './permissions';

export const REPORT_VIEW_PERMISSIONS = {
  PEOPLE: PERMISSIONS.REPORTS_VIEW_PEOPLE,
  ATTENDANCE: PERMISSIONS.REPORTS_VIEW_ATTENDANCE,
  GROUPS: PERMISSIONS.REPORTS_VIEW_GROUPS,
  PROGRAMS: PERMISSIONS.REPORTS_VIEW_PROGRAMS,
  MESSAGING: PERMISSIONS.REPORTS_VIEW_MESSAGING,
  GIVING: PERMISSIONS.REPORTS_VIEW_GIVING,
  SPENDING: PERMISSIONS.REPORTS_VIEW_SPENDING,
  PROJECTS: PERMISSIONS.REPORTS_VIEW_PROJECTS,
  WELFARE: PERMISSIONS.REPORTS_VIEW_WELFARE,
  ASSETS: PERMISSIONS.REPORTS_VIEW_ASSETS,
  PASTORAL: PERMISSIONS.REPORTS_VIEW_PASTORAL,
  PRAYER: PERMISSIONS.REPORTS_VIEW_PRAYER,
} as const;

const NON_FINANCIAL_REPORT_VIEW_PERMISSIONS: readonly PermissionCode[] = [
  REPORT_VIEW_PERMISSIONS.PEOPLE,
  REPORT_VIEW_PERMISSIONS.ATTENDANCE,
  REPORT_VIEW_PERMISSIONS.GROUPS,
  REPORT_VIEW_PERMISSIONS.PROGRAMS,
  REPORT_VIEW_PERMISSIONS.MESSAGING,
  REPORT_VIEW_PERMISSIONS.ASSETS,
  REPORT_VIEW_PERMISSIONS.PASTORAL,
  REPORT_VIEW_PERMISSIONS.PRAYER,
];

const FINANCIAL_REPORT_VIEW_PERMISSIONS: readonly PermissionCode[] = [
  REPORT_VIEW_PERMISSIONS.GIVING,
  REPORT_VIEW_PERMISSIONS.SPENDING,
  REPORT_VIEW_PERMISSIONS.PROJECTS,
  REPORT_VIEW_PERMISSIONS.WELFARE,
];

export const ALL_REPORT_VIEW_PERMISSIONS: readonly PermissionCode[] = [
  ...NON_FINANCIAL_REPORT_VIEW_PERMISSIONS,
  ...FINANCIAL_REPORT_VIEW_PERMISSIONS,
];

export const FINANCIAL_REPORT_ACCESS_PERMISSIONS: readonly PermissionCode[] = [
  PERMISSIONS.REPORTS_VIEW_FINANCIAL,
  ...FINANCIAL_REPORT_VIEW_PERMISSIONS,
];

export const REPORT_ROUTE_ACCESS_PERMISSIONS: readonly PermissionCode[] = [
  PERMISSIONS.REPORTS_VIEW,
  PERMISSIONS.REPORTS_VIEW_FINANCIAL,
  PERMISSIONS.DASHBOARDS_VIEW,
  ...ALL_REPORT_VIEW_PERMISSIONS,
];

type MembershipLike = Pick<CurrentMembership, 'systemRole' | 'permissions'> | null | undefined;

export const hasReportViewPermission = (
  permissions: readonly (PermissionCode | string)[],
  required: PermissionCode | string,
  systemRole?: string,
): boolean => {
  if (systemRole === 'owner' || systemRole === 'admin') return true;
  if (permissions.includes(required)) return true;

  if (
    (NON_FINANCIAL_REPORT_VIEW_PERMISSIONS as readonly string[]).includes(required) &&
    permissions.includes(PERMISSIONS.REPORTS_VIEW)
  ) {
    return true;
  }

  if (
    (FINANCIAL_REPORT_VIEW_PERMISSIONS as readonly string[]).includes(required) &&
    permissions.includes(PERMISSIONS.REPORTS_VIEW_FINANCIAL)
  ) {
    return true;
  }

  return false;
};

export const hasAnyReportViewPermission = (
  membership: MembershipLike,
  candidates: readonly (PermissionCode | string)[],
): boolean => {
  if (!membership) return false;
  return candidates.some((code) =>
    hasReportViewPermission(membership.permissions, code, membership.systemRole),
  );
};

export const canAccessReportsModule = (membership: MembershipLike): boolean => {
  if (!membership) return false;
  return REPORT_ROUTE_ACCESS_PERMISSIONS.some((code) =>
    hasReportViewPermission(membership.permissions, code, membership.systemRole),
  );
};

export const canViewFinancialReports = (membership: MembershipLike): boolean => {
  if (!membership) return false;
  return FINANCIAL_REPORT_VIEW_PERMISSIONS.some((code) =>
    hasReportViewPermission(membership.permissions, code, membership.systemRole),
  );
};
